/**
 * Base Configuration Service
 * 
 * Abstract base class providing common configuration management functionality
 */
class BaseConfigService {
  constructor() {
    if (this.constructor === BaseConfigService) {
      throw new Error('BaseConfigService is abstract and cannot be instantiated directly');
    }
    
    this._config = this._getDefaultConfig();
    this._observers = new Map();
    this._isInitialized = false;
  }

  /**
   * Abstract method - must be implemented by subclasses
   */
  _getDefaultConfig() {
    throw new Error('_getDefaultConfig() must be implemented by subclass');
  }

  /**
   * Initialize the configuration service
   */
  initialize(config = {}) {
    if (!this._validateConfig(config)) {
      return false;
    }

    this._applyConfig(config);
    
    // Custom validation hook for subclasses
    if (this._validateInitialization && !this._validateInitialization()) {
      return false;
    }

    this._isInitialized = true;
    this._notifyObservers('initialized', { config: this._getImmutableConfig() });
    return true;
  }

  /**
   * Update configuration with validation
   */
  updateConfig(config) {
    if (!this._validateConfig(config)) {
      throw new Error('Invalid configuration provided');
    }

    const oldConfig = { ...this._config };
    this._applyConfig(config);
    
    this._notifyObservers('configUpdated', { 
      oldConfig: this._deepFreeze(oldConfig), 
      newConfig: this._getImmutableConfig() 
    });
  }

  /**
   * Apply configuration - can be overridden by subclasses
   */
  _applyConfig(config) {
    // Default implementation - direct property assignment
    Object.keys(config).forEach(key => {
      if (typeof config[key] !== 'undefined') {
        this._config[key] = config[key];
      }
    });
  }

  /**
   * Validate configuration - can be overridden by subclasses
   */
  _validateConfig(config) {
    if (!config || typeof config !== 'object') {
      return false;
    }
    return true;
  }

  /**
   * Get specific configuration value
   */
  get(key) {
    return this._deepFreeze(this._config[key]);
  }

  /**
   * Get all configuration
   */
  getConfig() {
    return this._getImmutableConfig();
  }

  /**
   * Check if service is initialized
   */
  isInitialized() {
    return this._isInitialized;
  }

  /**
   * Subscribe to configuration changes
   */
  subscribe(event, callback) {
    if (!this._observers.has(event)) {
      this._observers.set(event, []);
    }
    
    this._observers.get(event).push(callback);
    
    // Return unsubscribe function
    return () => {
      const callbacks = this._observers.get(event);
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    };
  }

  /**
   * Notify observers of changes
   */
  _notifyObservers(event, data) {
    const callbacks = this._observers.get(event);
    if (callbacks) {
      callbacks.forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in ${this.constructor.name} configuration observer:`, error);
        }
      });
    }
  }

  /**
   * Get deep frozen copy of config
   */
  _getImmutableConfig() {
    return this._deepFreeze({ ...this._config });
  }

  /**
   * Deep freeze object to prevent mutations
   */
  _deepFreeze(obj) {
    if (obj === null || typeof obj !== 'object') {
      return obj;
    }

    Object.getOwnPropertyNames(obj).forEach(prop => {
      const value = obj[prop];
      if (value && typeof value === 'object') {
        this._deepFreeze(value);
      }
    });

    return Object.freeze(obj);
  }

  /**
   * Reset configuration to defaults
   */
  reset() {
    this._config = this._getDefaultConfig();
    this._isInitialized = false;
    this._notifyObservers('reset', {});
  }

  /**
   * Check if configuration meets specific criteria - can be overridden
   */
  isConfigured() {
    return Object.values(this._config).some(value => 
      value !== null && value !== undefined && value !== ''
    );
  }
}

module.exports = BaseConfigService;